import { attendanceValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionConstants'
import type { endOfSessionMetadataValues } from '@lyrahealth-inc/shared-app-logic/src/features/sessions/endOfSession/endOfSessionMetadata'
import { ICD10 } from '@lyrahealth-inc/shared-app-logic/src/models/icd10/ICD10'

/**
 * Form values from the consolidated DA Flexible Note.
 *
 * The close-session fields in daFlexibleNote.json now use the canonical end-of-session keys
 * (e.g. `modality`, `session_duration`, `primary_condition`), so no field-name translation is
 * needed. This type just documents the close-session subset; the form also carries note-only
 * fields (subjective, plan, interventions, safety, etc.) under other keys.
 *
 * `icd10_primary_diagnosis`/`icd10_secondary_diagnosis` are captured by the note's `fromContext: icd10s`
 * typeahead, which stores each selection as its option *label* (`"<code> <desc>"`) rather than the
 * ICD10 object — so they arrive here as strings and are resolved back into ICD10 objects below.
 */
export type FlexNoteFormValues = {
  attendance?: string
  modality?: string
  session_duration?: string
  reference_number?: string
  primary_condition?: string[]
  secondary_condition?: string[] | null
  other_primary_description?: string
  other_secondary_description?: string
  icd10_primary_diagnosis?: string | string[]
  icd10_secondary_diagnosis?: string | string[]
  changeReason?: string
  changeNotice?: string
  otherChangeReason?: string
  technicalIssuesCancelSelection?: string
  technicalIssuesCancelDetails?: string
  guardianConsent?: boolean
  is_last_session?: boolean
  episode_state?: string
  termination_comment?: string
  attestation_pre_authorization?: string
  [key: string]: unknown
}

/**
 * Resolves the icd10 typeahead's stored label(s) back into the ICD10 objects the charge expects.
 *
 * The `icd10s` typeahead stores each selection as its display label (`"<code> <desc>"`), but
 * `getBCCharge` serializes icd10 fields via `.map((o) => o.code)`. We rebuild the objects by
 * matching each stored label against the same icd10 option list that produced them. Returns
 * `undefined` when nothing resolves, so the caller can omit the key (mirroring the standalone
 * end-of-session metadata) and `getBCCharge` never maps over `undefined`.
 */
const resolveICD10s = (selected: string | string[] | undefined, byLabel: Map<string, ICD10>): ICD10[] | undefined => {
  if (selected == null) return undefined
  const labels = Array.isArray(selected) ? selected : [selected]
  const resolved = labels.map((label) => byLabel.get(label)).filter((entry): entry is ICD10 => Boolean(entry))
  return resolved.length ? resolved : undefined
}

// A non-blank string the provider actually entered/selected (radios, text, modality JSON).
const hasText = (value: unknown): value is string => typeof value === 'string' && value.trim() !== ''

// A non-empty selection array (typeahead-backed fields like primary/secondary condition).
const hasItems = (value: unknown): value is string[] => Array.isArray(value) && value.length > 0

/**
 * Picks the close-session subset of the flexible note's form values into the shape
 * `useCloseSessionSubmit` / `getBCCharge` expect.
 *
 * This is a whitelist pick on purpose: `getBCCharge` appends every key it's handed onto the
 * charge, so the note-only fields must NOT be forwarded. Because the form already uses the
 * canonical keys, the close-session values pass straight through (no renaming); only the icd10
 * fields are translated from their stored labels back into ICD10 objects (via `icd10Options`,
 * the `icd10s.all` list that populated the typeahead).
 */
export const mapFlexibleNoteValuesToCloseSessionInput = (
  flexNoteValues: FlexNoteFormValues,
  icd10Options: ICD10[] = [],
): endOfSessionMetadataValues => {
  const icd10ByLabel = new Map(icd10Options.map((option) => [`${option.code} ${option.desc}`, option]))
  const primaryICD10s = resolveICD10s(flexNoteValues.icd10_primary_diagnosis, icd10ByLabel)
  const secondaryICD10s = resolveICD10s(flexNoteValues.icd10_secondary_diagnosis, icd10ByLabel)

  // Only forward fields the provider actually filled. `getBCCharge` appends every key it's handed
  // onto the charge, so emitting unset fields would send `reference_number=undefined`,
  // `attendanceIneligibility=false`, empty `other_*_description`, etc. The standalone end-of-session
  // form omits unset fields (its values come straight from the form), and we match that here.
  return {
    attendance: (flexNoteValues.attendance as string) ?? attendanceValues.ATTENDED,
    ...(hasText(flexNoteValues.changeReason) && { changeReason: flexNoteValues.changeReason }),
    ...(hasText(flexNoteValues.changeNotice) && { changeNotice: flexNoteValues.changeNotice }),
    ...(hasText(flexNoteValues.otherChangeReason) && { otherChangeReason: flexNoteValues.otherChangeReason }),
    ...(hasText(flexNoteValues.technicalIssuesCancelSelection) && {
      technicalIssuesCancelSelection: flexNoteValues.technicalIssuesCancelSelection,
    }),
    ...(hasText(flexNoteValues.technicalIssuesCancelDetails) && {
      technicalIssuesCancelDetails: flexNoteValues.technicalIssuesCancelDetails,
    }),
    ...(hasText(flexNoteValues.modality) && { modality: flexNoteValues.modality }),
    ...(hasText(flexNoteValues.session_duration) && { session_duration: flexNoteValues.session_duration }),
    ...(hasText(flexNoteValues.reference_number) && { reference_number: flexNoteValues.reference_number }),
    ...(hasItems(flexNoteValues.primary_condition) && { primary_condition: flexNoteValues.primary_condition }),
    ...(hasItems(flexNoteValues.secondary_condition) && { secondary_condition: flexNoteValues.secondary_condition }),
    ...(hasText(flexNoteValues.other_primary_description) && {
      other_primary_description: flexNoteValues.other_primary_description,
    }),
    ...(hasText(flexNoteValues.other_secondary_description) && {
      other_secondary_description: flexNoteValues.other_secondary_description,
    }),
    ...(primaryICD10s && { icd10_primary_diagnosis: primaryICD10s }),
    ...(secondaryICD10s && { icd10_secondary_diagnosis: secondaryICD10s }),
    ...(flexNoteValues.guardianConsent != null && { guardianConsent: flexNoteValues.guardianConsent }),
    ...(flexNoteValues.is_last_session != null && { is_last_session: flexNoteValues.is_last_session }),
    ...(flexNoteValues.is_last_session &&
      hasText(flexNoteValues.episode_state) && { episode_state: flexNoteValues.episode_state }),
    ...(flexNoteValues.is_last_session &&
      hasText(flexNoteValues.termination_comment) && { termination_comment: flexNoteValues.termination_comment }),
    ...(hasText(flexNoteValues.attestation_pre_authorization) && {
      attestation_pre_authorization: flexNoteValues.attestation_pre_authorization,
    }),
    ...pickAttestationDataFields(flexNoteValues),
  }
}

const pickAttestationDataFields = (values: FlexNoteFormValues): Record<string, unknown> => {
  const result: Record<string, unknown> = {}
  for (const key of Object.keys(values)) {
    if (key.startsWith('attestation_data.') && values[key] != null) {
      result[key] = values[key]
    }
  }
  return result
}
